<section class="chatbot-widget" aria-label="FlowDrizzle assistant">
  @if (isOpen) {
    <div class="chat-window" role="dialog" aria-modal="false" aria-labelledby="flowdrizzle-chat-title">
      <header class="chat-header">
        <div class="chat-header__brand">
          <div class="chat-avatar" aria-hidden="true">
            <mat-icon>support_agent</mat-icon>
          </div>

          <div class="chat-header__copy">
            <h2 id="flowdrizzle-chat-title">FlowDrizzle Assistant</h2>
          </div>
        </div>

        <div class="chat-header__actions" aria-label="Chat actions">
          <button
            mat-icon-button
            class="chat-header__icon-button"
            type="button"
            [attr.aria-label]="isSoundEnabled ? 'Turn assistant sound off' : 'Turn assistant sound on'"
            (click)="toggleSound()"
          >
            <mat-icon>{{ isSoundEnabled ? 'volume_up' : 'volume_off' }}</mat-icon>
          </button>

          <button mat-icon-button type="button" aria-label="Refresh local chat session" (click)="refreshChat()">
            <mat-icon>refresh</mat-icon>
          </button>

          <button mat-icon-button type="button" aria-label="Clear saved chat session" (click)="clearChat()">
            <mat-icon>delete_outline</mat-icon>
          </button>

          <button mat-icon-button type="button" aria-label="Close assistant" (click)="toggleChat()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </header>

      <div #messagePanel class="chat-messages" aria-live="polite" aria-relevant="additions">
        @if (!hasConversation) {
          <article class="chat-empty">
            <div class="chat-empty__icon" aria-hidden="true">
              <mat-icon>forum</mat-icon>
            </div>
            <h3>How can I help?</h3>
            <p>Ask about features, pricing, demos, forms, pipeline, automation, reports, or support.</p>
          </article>
        }

        @for (message of messages; track trackByMessageId($index, message)) {
          <article
            class="chat-message"
            [class.chat-message--user]="message.sender === 'user'"
            [class.chat-message--bot]="message.sender === 'bot'"
          >
            <div class="chat-message__avatar" aria-hidden="true">
              <mat-icon>{{ message.sender === 'user' ? 'person' : 'smart_toy' }}</mat-icon>
            </div>

            <div class="chat-message__content">
              <p>{{ message.text }}</p>

              @if (message.links?.length) {
                <div class="source-links" aria-label="Related links">
                  @for (link of message.links; track link.url) {
                    @if (isInternalLink(link.url)) {
                      <a [routerLink]="link.url" (click)="isOpen = false">
                        <span>{{ link.label }}</span>
                        <small>{{ link.description }}</small>
                      </a>
                    } @else {
                      <a [href]="link.url" target="_blank" rel="noopener">
                        <span>{{ link.label }}</span>
                        <small>{{ link.description }}</small>
                      </a>
                    }
                  }
                </div>
              }
            </div>
          </article>
        }

        @if (isTyping) {
          <article class="chat-message chat-message--bot" aria-label="Assistant is typing">
            <div class="chat-message__avatar" aria-hidden="true">
              <mat-icon>smart_toy</mat-icon>
            </div>

            <div class="chat-message__content typing-bubble">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </article>
        }
      </div>

      <div class="chat-footer">
        @if (history.length) {
          <div class="history-strip" aria-label="Saved Q&A">
            <span>Recent Q&A</span>
            <div>
              @for (item of history.slice(0, 3); track trackByHistoryId($index, item)) {
                <button mat-button type="button" (click)="askFromHistory(item)" [disabled]="isTyping">
                  {{ item.question }}
                </button>
              }
            </div>
          </div>
        }

        <div class="suggestions" aria-label="Suggested questions">
          @for (suggestion of suggestions.slice(0, 4); track suggestion) {
            <button mat-stroked-button type="button" (click)="askSuggestion(suggestion)" [disabled]="isTyping">
              {{ suggestion }}
            </button>
          }
        </div>

        <form class="chat-input" (ngSubmit)="askQuestion()" novalidate>
  <mat-form-field appearance="outline" subscriptSizing="dynamic">
    <mat-label>Ask a question</mat-label>

    <input
      #questionInput
      matInput
      [formControl]="questionControl"
      maxlength="250"
      autocomplete="off"
      placeholder="Ask about pipeline, pricing, automation..."
      [disabled]="isTyping"
      (keydown)="onInputKeydown($event)"
      aria-describedby="question-hint"
    />

    <mat-hint id="question-hint" align="end">
      {{ questionControl.value.length }}/250
    </mat-hint>

    @if (questionControl.touched && questionControl.hasError('required')) {
      <mat-error>Enter a question to continue.</mat-error>
    }

    @if (questionControl.touched && questionControl.hasError('minlength')) {
      <mat-error>Please enter at least 3 characters.</mat-error>
    }

    @if (questionControl.touched && questionControl.hasError('maxlength')) {
      <mat-error>Keep your question under 250 characters.</mat-error>
    }
  </mat-form-field>

  <button
    mat-mini-fab
    color="primary"
    type="submit"
    aria-label="Send question"
    [disabled]="questionControl.invalid || isTyping"
  >
    <mat-icon>{{ isTyping ? 'hourglass_top' : 'arrow_forward' }}</mat-icon>
  </button>
</form>

      </div>
    </div>
  }

  <button
    mat-fab
    color="primary"
    class="chat-toggle"
    type="button"
    [attr.aria-label]="isOpen ? 'Close FlowDrizzle assistant' : 'Open FlowDrizzle assistant'"
    [attr.aria-expanded]="isOpen"
    (click)="toggleChat()"
  >
    <mat-icon>{{ isOpen ? 'close' : 'support_agent' }}</mat-icon>
  </button>
</section>
